import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useContent } from '@/hooks/useContent';
import { SafeHtml } from '@/components/common/SafeHtml';
import { type LegalLink } from '@/services/legalService';

interface Props {
  link: LegalLink | null;
  onClose: () => void;
}

/**
 * Modal viewer for inline legal document content (documents without an
 * external URL). Renders document_title and description HTML safely via SafeHtml.
 */
export function LegalDocumentModal({ link, onClose }: Readonly<Props>) {
  const { getContent, getContentString } = useContent();
  const open = link !== null && !!link.content;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="legal-doc-title"
    >
      <DialogTitle id="legal-doc-title" sx={{ pr: 6 }}>
        <SafeHtml html={link?.documentTitle ?? ''} component="span" />
      </DialogTitle>
      <IconButton
        onClick={onClose}
        aria-label={getContentString('PortalClose')}
        size="small"
        sx={{ position: 'absolute', top: 12, right: 12, color: 'text.secondary' }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent dividers>
        {link?.content ? (
          <SafeHtml
            html={link.content}
            sx={{ typography: 'body2', '& a': { color: 'primary.main' }, '& p': { mb: 1 }, '& ul,& ol': { pl: 3, mb: 1 } }}
          />
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: 2.5 }}>
          {getContent('PortalClose')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
